import React, { useState } from 'react';
import { Search, MapPin, ChevronDown, Tag, Stethoscope, Brain, Activity, Ambulance, Microscope, Pill, Cross, Shield, Zap, Eye, Baby, Armchair as Wheelchair, Leaf, Smile, Dumbbell, X, Building, Users, Check, Plus, Heart, Filter, ChevronUp, Globe, Calendar } from '../lib/icons';
import { ProviderType } from '../lib/supabase';

interface FilterSectionProps {
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  selectedSpecialtyIds: string[];
  onSpecialtyIdsChange: (specialtyIds: string[]) => void;
  selectedTopicIds: string[];
  onTopicIdsChange: (topicIds: string[]) => void;
  selectedCities: string[];
  onCitiesChange: (cities: string[]) => void;
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  specialties: any[];
  topics: any[];
  fromBlogPost?: boolean;
  blogPostTitle?: string;
  onClearAllFilters?: () => void;
  selectedInsuranceCoverage?: string[];
  onInsuranceCoverageChange?: (coverage: string[]) => void;
  selectedLanguages?: string[];
  onLanguagesChange?: (languages: string[]) => void;
  onlineBookingOnly?: boolean;
  onOnlineBookingOnlyChange?: (enabled: boolean) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  searchTerm,
  onSearchTermChange,
  selectedSpecialtyIds,
  onSpecialtyIdsChange,
  selectedTopicIds,
  onTopicIdsChange,
  selectedCities,
  onCitiesChange,
  selectedTypes,
  onTypesChange,
  specialties,
  topics,
  fromBlogPost,
  blogPostTitle,
  onClearAllFilters,
  selectedInsuranceCoverage = [],
  onInsuranceCoverageChange,
  selectedLanguages = [],
  onLanguagesChange,
  onlineBookingOnly = false,
  onOnlineBookingOnlyChange
}) => {
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [specialtySearchTerm, setSpecialtySearchTerm] = useState('');
  const [topicSearchTerm, setTopicSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);


  const providerTypes = [
    { value: 'doctor', label: 'Arzt / Ärztin', icon: Stethoscope },
    { value: 'group_practice', label: 'Gemeinschaftspraxis', icon: Users },
    { value: 'clinic', label: 'Klinik / Krankenhaus', icon: Building },
    { value: 'naturopath', label: 'Heilpraktiker / Heilpraktikerin', icon: Leaf },
    { value: 'psychotherapist', label: 'Psychotherapeut / Psychotherapeutin', icon: Brain },
    { value: 'therapy_practice', label: 'Therapeutische Praxis', icon: Activity },
    { value: 'ambulanz', label: 'Ambulantes OP-Zentrum / Tagesklinik', icon: Ambulance },
    { value: 'fachklinik', label: 'Fachklinik / Spezialklinik', icon: Cross },
    { value: 'labor_diagnostik', label: 'Labor / Diagnostikzentrum', icon: Microscope },
    { value: 'mvz', label: 'MVZ (Medizinisches Versorgungszentrum)', icon: Shield },
    { value: 'naturheilpraxis', label: 'Naturheilpraxis / Komplementärmedizinisches Zentrum', icon: Heart },
    { value: 'pflegedienst', label: 'Pflegedienst / Pflegeeinrichtung', icon: Wheelchair },
    { value: 'reha', label: 'Rehaklinik / Rehabilitationszentrum', icon: Zap },
    { value: 'dentist', label: 'Zahnärztliche Einrichtung', icon: Smile },
    { value: 'sonstiges', label: 'Sonstiges', icon: Eye }
  ];

  const cities = [
    'Aachen', 'Augsburg', 'Berlin', 'Bielefeld', 'Bochum', 'Bonn', 'Bremen',
    'Dortmund', 'Dresden', 'Düsseldorf', 'Duisburg', 'Erfurt', 'Erlangen',
    'Essen', 'Frankfurt', 'Freiburg', 'Gelsenkirchen', 'Hagen', 'Halle',
    'Hamburg', 'Hamm', 'Hannover', 'Heidelberg', 'Heilbronn', 'Herne',
    'Hildesheim', 'Ingolstadt', 'Jena', 'Kaiserslautern', 'Karlsruhe',
    'Kassel', 'Kiel', 'Koblenz', 'Köln', 'Krefeld', 'Leipzig', 'Leverkusen',
    'Lübeck', 'Ludwigshafen', 'Magdeburg', 'Mainz', 'Mannheim', 'Moers',
    'Mönchengladbach', 'Mülheim', 'München', 'Münster', 'Neuss', 'Nürnberg',
    'Oberhausen', 'Offenbach', 'Oldenburg', 'Osnabrück', 'Paderborn',
    'Pforzheim', 'Potsdam', 'Recklinghausen', 'Regensburg', 'Remscheid',
    'Reutlingen', 'Rostock', 'Saarbrücken', 'Salzgitter', 'Siegen',
    'Solingen', 'Stuttgart', 'Trier', 'Ulm', 'Wiesbaden', 'Wolfsburg',
    'Wuppertal', 'Würzburg'
  ];

  // Available insurance coverage options
  const availableInsuranceCoverage = [
    'Alle gesetzlichen Krankenkassen',
    'Private Krankenversicherung (PKV)',
    'Selbstzahler',
    'Heilpraktiker-Zusatzversicherung',
    'Keine Kassenleistung',
    'Auf Anfrage prüfbar'
  ];

  // Available languages
  const availableLanguages = [
    'Deutsch',
    'Englisch',
    'Französisch',
    'Spanisch',
    'Italienisch',
    'Türkisch',
    'Russisch',
    'Polnisch',
    'Arabisch',
    'Chinesisch'
  ];

  // Filter functions for search
  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(citySearchTerm.toLowerCase())
  );

  const filteredTypes = providerTypes.filter(type =>
    type.label.toLowerCase().includes(typeSearchTerm.toLowerCase())
  );

  const filteredSpecialties = specialties.filter(specialty =>
    specialty.name.toLowerCase().includes(specialtySearchTerm.toLowerCase())
  );

  const filteredTopics = topics.filter(topic =>
    topic.name.toLowerCase().includes(topicSearchTerm.toLowerCase())
  );

  const handleCityToggle = (city: string) => {
    if (selectedCities.includes(city)) {
      onCitiesChange(selectedCities.filter(c => c !== city));
    } else {
      onCitiesChange([...selectedCities, city]);
    }
  };

  const handleTypeToggle = (type: string) => {
    if (selectedTypes.includes(type)) {
      onTypesChange(selectedTypes.filter(t => t !== type));
    } else {
      onTypesChange([...selectedTypes, type]);
    }
  };

  const handleSpecialtyToggle = (specialtyId: string) => {
    if (selectedSpecialtyIds.includes(specialtyId)) {
      onSpecialtyIdsChange(selectedSpecialtyIds.filter(id => id !== specialtyId));
    } else {
      onSpecialtyIdsChange([...selectedSpecialtyIds, specialtyId]);
    }
  };

  const handleTopicToggle = (topicId: string) => {
    if (selectedTopicIds.includes(topicId)) {
      onTopicIdsChange(selectedTopicIds.filter(id => id !== topicId));
    } else {
      onTopicIdsChange([...selectedTopicIds, topicId]);
    }
  };

  const handleInsuranceCoverageToggle = (coverage: string) => {
    if (!onInsuranceCoverageChange) return;
    
    if (selectedInsuranceCoverage.includes(coverage)) {
      onInsuranceCoverageChange(selectedInsuranceCoverage.filter(c => c !== coverage));
    } else {
      onInsuranceCoverageChange([...selectedInsuranceCoverage, coverage]);
    }
  };

  const handleLanguageToggle = (language: string) => {
    if (!onLanguagesChange) return;
    
    if (selectedLanguages.includes(language)) {
      onLanguagesChange(selectedLanguages.filter(l => l !== language));
    } else {
      onLanguagesChange([...selectedLanguages, language]);
    }
  };

  const clearAllFilters = () => {
    onSearchTermChange('');
    onCitiesChange([]);
    onTypesChange([]);
    onSpecialtyIdsChange([]);
    onTopicIdsChange([]);
    setShowCityDropdown(false);
    setShowTypeDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowTopicDropdown(false);
    setCitySearchTerm('');
    setTypeSearchTerm('');
    setSpecialtySearchTerm('');
    setTopicSearchTerm('');
    
    // Clear blog post filter state from navigation
    if (window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const getSelectedTypeLabels = () => {
    return selectedTypes.map(type => 
      providerTypes.find(pt => pt.value === type)?.label || type
    );
  };

  const getSelectedSpecialtyNames = () => {
    return selectedSpecialtyIds.map(id => 
      specialties.find(s => s.id === id)?.name || 'Unbekannt'
    );
  };

  const getSelectedTopicNames = () => {
    return selectedTopicIds.map(id => 
      topics.find(t => t.id === id)?.name || 'Unbekannt'
    );
  };

  const hasAdvancedFilters = selectedInsuranceCoverage.length > 0 || 
                            selectedLanguages.length > 0 || 
                            onlineBookingOnly;

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-8">
      <div className="flex flex-col gap-4">
        {/* Main Search */}
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              placeholder="Arzt, Fachrichtung oder Krankheit suchen..."
              className="w-full pl-4 pr-16 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            />
            <button
              type="button"
              onClick={() => {
                const input = document.querySelector('input[placeholder*="Arzt, Fachrichtung"]') as HTMLInputElement;
                if (input) input.focus();
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
              aria-label="Suche starten"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Filter Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Type Filter with Multi-Select */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
              aria-label="Anbietertyp-Filter öffnen"
              aria-expanded={showTypeDropdown}
              aria-haspopup="listbox"
            >
              <div className="flex items-center">
                <Building className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {selectedTypes.length === 0 
                    ? 'Anbietertyp'
                    : selectedTypes.length === 1
                    ? getSelectedTypeLabels()[0]
                    : `${selectedTypes.length} Typen ausgewählt`
                  }
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showTypeDropdown && (
              <>
                <div
                  className="fixed inset-0 z-5"
                  onClick={() => setShowTypeDropdown(false)}
                />
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={typeSearchTerm}
                        onChange={(e) => setTypeSearchTerm(e.target.value)}
                        placeholder="Anbietertyp suchen..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto">
                    {filteredTypes.length > 0 ? (
                      filteredTypes.map(type => (
                        <label
                          key={type.value}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(type.value)}
                            onChange={() => handleTypeToggle(type.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                          />
                          <type.icon className="w-4 h-4 mr-2" />
                          {type.label}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Keine Anbietertypen gefunden
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Specialty Filter with Multi-Select */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
              aria-label="Fachgebiet-Filter öffnen"
              aria-expanded={showSpecialtyDropdown}
              aria-haspopup="listbox"
            >
              <div className="flex items-center">
                <Stethoscope className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {selectedSpecialtyIds.length === 0 
                    ? 'Fachgebiet'
                    : selectedSpecialtyIds.length === 1
                    ? getSelectedSpecialtyNames()[0]
                    : `${selectedSpecialtyIds.length} Fachgebiete ausgewählt`
                  }
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showSpecialtyDropdown && (
              <>
                <div
                  className="fixed inset-0 z-5"
                  onClick={() => setShowSpecialtyDropdown(false)}
                />
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={specialtySearchTerm}
                        onChange={(e) => setSpecialtySearchTerm(e.target.value)}
                        placeholder="Fachgebiet suchen..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto">
                    {filteredSpecialties.length > 0 ? (
                      filteredSpecialties.map(specialty => (
                        <label
                          key={specialty.id}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSpecialtyIds.includes(specialty.id)}
                            onChange={() => handleSpecialtyToggle(specialty.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                          />
                          {specialty.name}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Keine Fachgebiete gefunden
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Topic Filter with Multi-Select */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTopicDropdown(!showTopicDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
              aria-label="Behandlungsthemen-Filter öffnen"
              aria-expanded={showTopicDropdown}
              aria-haspopup="listbox"
            >
              <div className="flex items-center">
                <Tag className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {selectedTopicIds.length === 0 
                    ? 'Thema'
                    : selectedTopicIds.length === 1
                    ? getSelectedTopicNames()[0]
                    : `${selectedTopicIds.length} Themen ausgewählt`
                  }
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showTopicDropdown && (
              <>
                <div
                  className="fixed inset-0 z-5"
                  onClick={() => setShowTopicDropdown(false)}
                />
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={topicSearchTerm}
                        onChange={(e) => setTopicSearchTerm(e.target.value)}
                        placeholder="Behandlungsthema suchen..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto">
                    {filteredTopics.length > 0 ? (
                      filteredTopics.filter(topic => topic.is_active).map(topic => (
                        <label
                          key={topic.id}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTopicIds.includes(topic.id)}
                            onChange={() => handleTopicToggle(topic.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                          />
                          {topic.name}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Keine Behandlungsthemen gefunden
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* City Filter with Multi-Select */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCityDropdown(!showCityDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
              aria-label="Stadt-Filter öffnen"
              aria-expanded={showCityDropdown}
              aria-haspopup="listbox"
            >
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {selectedCities.length === 0 
                    ? 'Stadt'
                    : selectedCities.length === 1
                    ? selectedCities[0]
                    : `${selectedCities.length} Städte ausgewählt`
                  }
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showCityDropdown && (
              <>
                <div
                  className="fixed inset-0 z-5"
                  onClick={() => setShowCityDropdown(false)}
                />
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={citySearchTerm}
                        onChange={(e) => setCitySearchTerm(e.target.value)}
                        placeholder="Stadt suchen..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto">
                    {filteredCities.length > 0 ? (
                      filteredCities.map(city => (
                        <label
                          key={city}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCities.includes(city)}
                            onChange={() => handleCityToggle(city)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                          />
                          {city}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Keine Städte gefunden
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Weitere Filter */}
          <div className="relative">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                hasAdvancedFilters
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              aria-label={showAdvancedFilters ? "Weitere Filter ausblenden" : "Weitere Filter einblenden"}
              aria-expanded={showAdvancedFilters}
            >
              <Filter className="w-5 h-5 mr-2" />
              Weitere Filter
              {hasAdvancedFilters && (
                <span className="ml-2 bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                  {selectedInsuranceCoverage.length + selectedLanguages.length + (onlineBookingOnly ? 1 : 0)}
                </span>
              )}
              {showAdvancedFilters ? (
                <ChevronUp className="w-5 h-5 ml-2" />
              ) : (
                <ChevronDown className="w-5 h-5 ml-2" />
              )}
            </button>
          </div>
        </div>

        {/* Advanced Filters Section */}
        {showAdvancedFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-6 animate-in slide-in-from-top duration-300">
            {/* Insurance Coverage Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2 text-blue-600" />
                Versicherungsarten
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {availableInsuranceCoverage.map((coverage) => (
                  <label
                    key={coverage}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInsuranceCoverage.includes(coverage)}
                      onChange={() => handleInsuranceCoverageToggle(coverage)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{coverage}</span>
                  </label>
                ))}
              </div>
              {selectedInsuranceCoverage.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedInsuranceCoverage.map((coverage) => (
                    <span
                      key={coverage}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {coverage}
                      <button
                        onClick={() => handleInsuranceCoverageToggle(coverage)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        aria-label={`Filter ${coverage} entfernen`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Languages Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Globe className="w-4 h-4 mr-2 text-green-600" />
                Gesprochene Sprachen
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {availableLanguages.map((language) => (
                  <label
                    key={language}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLanguages.includes(language)}
                      onChange={() => handleLanguageToggle(language)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{language}</span>
                  </label>
                ))}
              </div>
              {selectedLanguages.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedLanguages.map((language) => (
                    <span
                      key={language}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                    >
                      {language}
                      <button
                        onClick={() => handleLanguageToggle(language)}
                        className="ml-2 text-green-600 hover:text-green-800"
                        aria-label={`Sprache ${language} entfernen`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Online Booking Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                Online-Terminbuchung
              </label>
              <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={onlineBookingOnly}
                  onChange={(e) => onOnlineBookingOnlyChange?.(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="ml-3 text-sm text-gray-700">
                  Nur Anbieter mit Online-Terminbuchung anzeigen
                </span>
              </label>
              {onlineBookingOnly && (
                <div className="mt-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                    Online-Terminbuchung verfügbar
                    <button
                      onClick={() => onOnlineBookingOnlyChange?.(false)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                      aria-label="Online-Terminbuchung Filter entfernen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Selected Filters Display */}
        {(selectedTypes.length > 0 || selectedSpecialtyIds.length > 0 || selectedTopicIds.length > 0 || selectedCities.length > 0) && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
            {/* Selected Types */}
            {selectedTypes.map(type => {
              const typeData = providerTypes.find(pt => pt.value === type);
              return (
                <span
                  key={type}
                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  <Building className="w-3 h-3 mr-1" />
                  {typeData?.label}
                  <button
                    onClick={() => handleTypeToggle(type)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    aria-label={`Anbietertyp ${typeData?.label} entfernen`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}

            {/* Selected Specialties */}
            {selectedSpecialtyIds.map(specialtyId => {
              const specialty = specialties.find(s => s.id === specialtyId);
              return (
                <span
                  key={specialtyId}
                  className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                >
                  <Stethoscope className="w-3 h-3 mr-1" />
                  {specialty?.name}
                  <button
                    onClick={() => handleSpecialtyToggle(specialtyId)}
                    className="ml-2 text-green-600 hover:text-green-800"
                    aria-label={`Fachgebiet ${specialty?.name} entfernen`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}

            {/* Selected Topics */}
            {selectedTopicIds.map(topicId => {
              const topic = topics.find(t => t.id === topicId);
              return (
                <span
                  key={topicId}
                  className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {topic?.name}
                  <button
                    onClick={() => handleTopicToggle(topicId)}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                    aria-label={`Thema ${topic?.name} entfernen`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}

            {/* Selected Cities */}
            {selectedCities.map(city => (
              <span
                key={city}
                className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
              >
                <Tag className="w-3 h-3 mr-1" />
                {city}
                <button
                  onClick={() => handleCityToggle(city)}
                  className="ml-2 text-orange-600 hover:text-orange-800"
                  aria-label={`Stadt ${city} entfernen`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}


      </div>
      
      {/* Reset Filters Button */}
      {(searchTerm || selectedCities.length > 0 || selectedTypes.length > 0 || selectedSpecialtyIds.length > 0 || selectedTopicIds.length > 0 || hasAdvancedFilters) && (
        <div className="flex justify-center mt-6">
          <button
            onClick={onClearAllFilters || clearAllFilters}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
            aria-label="Alle Filter zurücketzen"
          >
            <X className="w-4 h-4 mr-2" />
            Alle Filter zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterSection;